<template>
  <component
    :is="tag"
    :class="[
      'Card',
      {
        'u-card-bordered': !elevation,
        'u-card-elevated': elevation,
        'is-disabled': disabled,
        [`Card--${status}`]: !!status,
      }
    ]"
  >
    <header v-if="title || $slots.header" class="Card-header">
      <div class="Card-titleWrapper">
        <h4 class="Card-title">
          <Icon
            v-if="headerIcon"
            class="Card-headerIcon"
            :icon="headerIcon"
            size="xl"
            :color="headerIconColor"
          />
          <slot name="header">
            {{ title }}
          </slot>
        </h4>

        <Tag
          v-if="statusText"
          size="sm"
          :type="tagType"
          variant="subtle-outlined"
        >
          {{ statusText }}
        </Tag>
      </div>

      <slot name="header-action" />
    </header>

    <p v-if="description" class="Card-description" v-html="description" />

    <slot />

    <div v-if="actionPrimary" class="Card-actions">
      <ActionButton
        variant="primary"
        @click="emit('clickPrimary')"
      >
        {{ actionPrimary }}
      </ActionButton>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ActionButton, Icon, Tag } from '@cvo/ui/components';
import type { TagType } from '@cvo/ui/components/atoms/Tag/Tag.model';
import type { CardProps, SupportedStatusColor } from './Card.model';

const props = withDefaults(defineProps<CardProps>(), {
  tag: 'div',
});

const emit = defineEmits<{
  clickPrimary: []
}>();

const STATUS_TO_TAG_TYPE: Record<SupportedStatusColor, TagType> = {
  danger: 'danger',
  inactive: 'neutral',
  success: 'success',
  warning: 'warning',
  info: 'information',
};

const tagType = computed(() => (props.status ? STATUS_TO_TAG_TYPE[props.status] : 'neutral'));

</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

$card-padding: vars.$organisms-card-details-lg-md-padding-top-bottom vars.$organisms-card-details-lg-md-padding-left-right;
$card-padding-sm: vars.$organisms-card-details-sm-padding-top-bottom vars.$organisms-card-details-sm-padding-left-right;

@layer cvo-ui {
  .Card {
    padding: $card-padding;
    position: relative;
    background-color: vars.$organisms-card-details-bg-color;
    border-radius: vars.$organisms-card-details-lg-md-border-radius;

    @include responsive.mobile {
      border-radius: vars.$organisms-card-details-sm-border-radius;
      padding: $card-padding-sm;
    }

    &--elevated {
      border: none;
      box-shadow: vars.$shadow-md;
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: vars.$dimensions-d-3;
    }

    &-titleWrapper {
      display: flex;
      gap: vars.$dimensions-d-3;
    }

    &-title {
      color: vars.$organisms-card-details-title-color;
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      @include text.heading-responsive(h5, h4);
    }

    &-content {
      // Flexible content area - styles determined by content
      flex: 1;
    }

    // Status styles
    &--danger {
      --status-color: #{vars.$color-system-status-danger-900};
    }

    &--inactive {
      --status-color: #{vars.$color-neutral-gray-500};
    }

    &-headerIcon {
      margin-inline-end: vars.$dimensions-d-2;
    }

    &-description {
      @include text.text(md);
      color: vars.$color-neutral-gray-800;
      margin-top: vars.$dimensions-d-6;
      margin-bottom: vars.$dimensions-d-4;
    }

    &-actions {
      display: flex;
      justify-content: end;
      flex-wrap: wrap;
      margin-top: vars.$dimensions-d-6;
      border-top: 1px solid vars.$color-neutral-gray-300;
      padding-top: vars.$dimensions-d-6;
    }
  }
}
</style>